/* eslint-disable max-lines */

import { NgTemplateOutlet } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  Signal,
  signal,
  ViewContainerRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import type { FormGroup } from '@angular/forms';
import { FormBuilder, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';

import { Actions } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import type { Observable } from 'rxjs';
import { firstValueFrom, lastValueFrom, map, race, take } from 'rxjs';

import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import { TellentCurrentUserDataFacade, TellentMembersDataFacade } from '@core/tellent-logic';
import type { Seat, TellentMember } from '@core/tellent-types';
import { SeatKind, TeamMemberStatus } from '@core/tellent-types';
import { TellentComponentsNotificationService, resolveMemberFullName } from '@core/tellent-util';
import {
  ButtonComponent,
  ButtonLoadingDirective,
  DialogBodyComponent,
  DialogFooterComponent,
  DialogRef,
  DialogService,
  DialogWidth,
  DividerComponent,
  FormFieldComponent,
  HeadingComponent,
  IconComponent,
  InputComponent,
  LabelComponent,
  ProtectedControlDirective,
  RtPlatform,
} from '@core/user-interface-legacy';

import { EditMemberEmailDialogComponent } from './edit-member-email-dialog/edit-member-email-dialog.component';
import { EditMemberStatusComponent } from './edit-member-status/edit-member-status.component';
import { AvatarInputComponent } from '../../avatar-input/avatar-input.component';

interface TellentEditMemberForm {
  firstName: FormControl<string>;
  lastName: FormControl<string>;
  email: FormControl<string>;
}

/*
 eslint-disable
  @typescript-eslint/consistent-type-assertions,
 @angular-eslint/no-inputs-metadata-property,
*/

@Component({
  selector: 'edit-member-account-tab',
  imports: [
    FormFieldComponent,
    InputComponent,
    ButtonComponent,
    ReactiveFormsModule,
    LabelComponent,
    DialogFooterComponent,
    TranslatePipe,
    DialogBodyComponent,
    HeadingComponent,
    IconComponent,
    NgTemplateOutlet,
    AvatarInputComponent,
    ButtonLoadingDirective,
    EditMemberStatusComponent,
    ProtectedControlDirective,
    DividerComponent,
  ],
  templateUrl: './edit-member-account-tab.component.html',
  styleUrl: './edit-member-account-tab.component.less',
  inputs: EditMemberAccountTabComponent.NgInputs,
})
export class EditMemberAccountTabComponent {
  public static NgInputs = ['memberGuid' as const];

  private readonly formBuilder = inject(FormBuilder);
  private readonly store = inject(Store);
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialogRef = inject(DialogRef);
  private readonly i18n = inject(I18nService);
  private readonly dialogService = inject(DialogService);
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly actions = inject(Actions);
  private readonly notificationsService = inject(TellentComponentsNotificationService);
  private readonly platform = inject(RtPlatform);
  private readonly currentUserFacade = inject(TellentCurrentUserDataFacade);
  private readonly membersDataFacade = inject(TellentMembersDataFacade);

  protected readonly activatedRoute = inject(ActivatedRoute);
  protected readonly TeamMemberStatus = TeamMemberStatus;

  public memberGuid: string;

  public memberData: Signal<TellentMember | null>;
  public memberStatus: Signal<TellentMember['status'] | null>;
  public memberVerified: Signal<boolean | null> = signal(false);

  public me = this.currentUserFacade.currentUser;
  public showDeactivate = computed(
    () => !(this.me()?.guid === this.memberGuid || this.memberData()?.owner),
  );

  public pending = signal(false);

  public translations: Record<TeamMemberStatus, string> = {
    [TeamMemberStatus.Active]: this.i18n.translate('tlc.members.edit_member.status.active'),
    [TeamMemberStatus.Invited]: this.i18n.translate('tlc.members.edit_member.status.invited'),
    [TeamMemberStatus.NotInvited]: this.i18n.translate(
      'tlc.members.edit_member.status.not_invited',
    ),
    [TeamMemberStatus.Deactivated]: this.i18n.translate(
      'tlc.members.edit_member.status.deactivated',
    ),
  };

  public form = this.formBuilder.nonNullable.group<TellentEditMemberForm>({
    firstName: new FormControl<string>('', { nonNullable: true }),
    lastName: new FormControl<string>('', { nonNullable: true }),
    email: new FormControl<string>('', { validators: Validators.email, nonNullable: true }),
  });

  public avatarFile: File | null;

  private updateFormWithUserData = effect(() => {
    const { firstName, lastName, email } = this.memberData()!;
    this.form.patchValue(
      {
        firstName,
        lastName,
        email,
      },
      { emitEvent: false },
    );
  });

  private updateStatus = effect(() => {
    this.disableFormFields();
    this.cdRef.detectChanges();
  });

  private fullName = computed(() => resolveMemberFullName(this.memberData()!));

  public ngOnInit() {
    this.memberData = this.membersDataFacade.teamMemberById(this.memberGuid);

    this.memberStatus = computed(() => this.memberData()?.status!);
    this.memberVerified = computed(
      () =>
        this.memberStatus() === TeamMemberStatus.Active ||
        this.memberStatus() === TeamMemberStatus.Deactivated,
    );

    this.disableFormFields();
  }

  public async submit() {
    this.pending.set(true);
    const results = await this.submitFormUpdate(this.form, this.avatarFile);
    this.dialogRef.close(results);
  }

  public cancel() {
    this.dialogRef.close();
  }
  public changeEmailClicked() {
    this.dialogService.openCustom(
      EditMemberEmailDialogComponent,
      {
        width: DialogWidth.SmallXX,
        mobileOptions: this.platform.IS_MOBILE
          ? {
              mobileModal: true,
            }
          : undefined,
      },
      { memberId: this.memberGuid },
      this.viewContainerRef,
    );
  }
  public sendPasswordResetInstructions() {
    this.dispatchPasswordReset();
  }

  public async activateMember(): Promise<void> {
    const confirmed = await firstValueFrom(this.openConfirmationDialog());
    if (confirmed) this.membersDataFacade.reactivateMember(this.memberGuid);
  }

  public sendInvitation() {
    this.membersDataFacade.sendInvitation(this.memberGuid);
  }

  public cacheAvatarImg(file: File | null): void {
    this.avatarFile = file;
    this.setFormToDirty();
  }

  public async deleteUserClicked() {
    const confirmed = await firstValueFrom(this.openDeleteConfirmationDialog());

    if (confirmed) {
      this.membersDataFacade.deleteMember(this.memberGuid);
      this.membersDataFacade.deleteMemberSuccess$.pipe(take(1)).subscribe(() => {
        this.notificationsService.success(
          this.i18n.translate('tlc.members.edit_member.delete_dialog.success_toast', {
            member: this.fullName(),
          }),
        );
        this.dialogRef.close();
      });
    }
  }

  public async deactivateUserClicked(): Promise<void> {
    const confirmed = await firstValueFrom(this.openDeactivateConfirmationDialog());
    if (confirmed) this.membersDataFacade.deactivateMember(this.memberData()!.guid);
  }

  public onStatusActionClicked(action: TeamMemberStatus) {
    // eslint-disable-next-line default-case
    switch (action) {
      case TeamMemberStatus.NotInvited:
        this.sendInvitation();
        break;
      case TeamMemberStatus.Invited:
        this.sendInvitation();
        break;
      case TeamMemberStatus.Active:
        this.sendPasswordResetInstructions();
        break;
      case TeamMemberStatus.Deactivated:
        this.activateMember();
        break;
    }
  }

  private async submitFormUpdate(form: FormGroup<TellentEditMemberForm>, avatar?: File | null) {
    const { firstName, lastName } = form.value;
    this.membersDataFacade.updateMember({
      firstName: firstName!,
      lastName: lastName!,
      email: form.controls.email.dirty ? form.value.email : undefined,
      avatar: avatar ? avatar : undefined,
      guid: this.memberGuid,
    });

    const result$ = race(
      this.membersDataFacade.updateMemberSuccess$,
      this.membersDataFacade.updateMemberFail$,
    ).pipe(
      take(1),
      takeUntilDestroyed(this.destroyRef),
      map(({ payload }) => {
        form.markAsPristine();
        return {
          success: !(payload instanceof HttpErrorResponse),
          payload,
        };
      }),
    );

    return lastValueFrom(result$);
  }

  private disableFormFields() {
    this.memberVerified() ? this.form.controls.email.disable() : this.form.controls.email.enable();
    this.memberStatus() === TeamMemberStatus.Deactivated
      ? this.form.controls.firstName.disable()
      : this.form.controls.firstName.enable();
    this.memberStatus() === TeamMemberStatus.Deactivated
      ? this.form.controls.lastName.disable()
      : this.form.controls.lastName.enable();
  }

  private dispatchPasswordReset() {
    this.membersDataFacade.sendPasswordReset(this.memberData()!.userGuid);
  }

  private findSeatRole(seats: Seat[], kind: SeatKind): string {
    const roleName = seats.find(seat => seat.app.kind === kind)?.role?.name;
    return roleName ? roleName : this.i18n.translate('tlc.common.no_access');
  }

  private openConfirmationDialog(): Observable<boolean> {
    const fullName = this.fullName();
    const seats = this.memberData()?.seats;

    return this.dialogService.information({
      title: this.i18n.translate('tlc.members.edit_member.actions.confirm_dialog.title', {
        member: fullName,
      }),
      message: this.i18n.translate('tlc.members.edit_member.actions.confirm_dialog.message', {
        member: fullName,
        recruitment: this.findSeatRole(seats!, SeatKind.Recruitment),
        coreHR: this.findSeatRole(seats!, SeatKind.CoreHR),
      }),
      agreeLabel: this.i18n.translate('tlc.members.edit_member.actions.confirm_dialog.agreeLabel'),
      mobileOptions: this.platform.IS_MOBILE
        ? {
            mobileModal: true,
          }
        : undefined,
    });
  }

  private openDeactivateConfirmationDialog(): Observable<boolean> {
    const fullName = this.fullName();
    return this.dialogService.warning({
      title: this.i18n.translate('tlc.members.edit_member.deactivate_dialog.title', {
        member: fullName,
      }),
      message: this.i18n.translate('tlc.members.edit_member.deactivate_dialog.message', {
        member: fullName,
      }),
      agreeLabel: this.i18n.translate('tlc.members.edit_member.deactivate_dialog.agreeLabel'),
      mobileOptions: this.platform.IS_MOBILE
        ? {
            mobileModal: true,
          }
        : undefined,
    });
  }

  private openDeleteConfirmationDialog(): Observable<boolean> {
    const fullName = this.fullName();
    return this.dialogService.error({
      title: this.i18n.translate('tlc.members.edit_member.delete_dialog.title', {
        member: fullName,
      }),
      message: this.i18n.translate('tlc.members.edit_member.delete_dialog.message', {
        member: fullName,
      }),
      agreeLabel: this.i18n.translate('tlc.members.edit_member.delete_dialog.agreeLabel'),
      mobileOptions: this.platform.IS_MOBILE
        ? {
            mobileModal: true,
          }
        : undefined,
    });
  }

  private setFormToDirty() {
    this.form.markAsDirty();
  }
}
